/**
 * Marco Gullo Termo Idraulica
 * Main JavaScript - Modular Architecture
 */

// ============================================================
// APP NAMESPACE
// ============================================================
const App = {
    config: {
        isMobile: window.innerWidth < 1200,
        isTouch: 'ontouchstart' in window,
    },

    lenis: null,

    init() {
        this.updateConfig();
        this.initSmoothScroll();
        this.initNavbar();
        this.initSwiper();
        this.initTeamSwiper();
        this.initPartnerSwiper();
        this.initRelatedSwiper();
        this.initExpertiseCards();
        this.initTabSection();
        this.initScrollToTop();
        this.initBlogFilters();
        this.initContactPills();

        GSAPAnimations.init();

        // Counter needs ScrollTrigger registered (done in GSAPAnimations.init)
        this.initCounterAnimation();

        window.addEventListener('resize', this.debounce(() => {
            this.updateConfig();
        }, 250));
    },

    updateConfig() {
        this.config.isMobile = window.innerWidth < 1200;
    },

    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    },

    // ========== SMOOTH SCROLL (Lenis) ==========
    initSmoothScroll() {
        if (this.config.isMobile || this.config.isTouch) return;
        if (typeof Lenis === 'undefined') return;

        this.lenis = new Lenis({
            duration: 1.2,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            direction: 'vertical',
            gestureDirection: 'vertical',
            smooth: true,
            smoothTouch: false,
            touchMultiplier: 2,
        });

        this.lenis.on('scroll', ScrollTrigger.update);

        gsap.ticker.add((time) => {
            this.lenis.raf(time * 1000);
        });

        gsap.ticker.lagSmoothing(0);

        document.querySelectorAll('a[href^="#"]').forEach(anchor => {
            anchor.addEventListener('click', (e) => {
                e.preventDefault();
                const target = document.querySelector(anchor.getAttribute('href'));
                if (target) {
                    this.lenis.scrollTo(target, {
                        offset: 0,
                        duration: 1.2,
                    });
                }
            });
        });
    },

    // ========== NAVBAR SCROLL EFFECT ==========
    initNavbar() {
        const navbar = document.querySelector('.navbar');
        if (!navbar) return;

        let lastScrollY = 0;
        const hasHero = navbar.classList.contains('navbar--hero');
        let isHeroVisible = hasHero;
        const scrollThreshold = 30; // deadzone to avoid jitter
        const heroSwitchPoint = 150; // px: switch from light to dark

        // Smart sticky: hide on scroll down, show on scroll up + hero mode
        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            // Hero mode toggle (only on front page)
            if (hasHero) {
                if (currentScrollY <= heroSwitchPoint) {
                    if (!isHeroVisible) {
                        navbar.classList.add('navbar--hero');
                        isHeroVisible = true;
                    }
                } else {
                    if (isHeroVisible) {
                        navbar.classList.remove('navbar--hero');
                        isHeroVisible = false;
                    }
                }
            }

            // Compact state
            if (currentScrollY > 50) {
                navbar.classList.add('navbar--scrolled');
            } else {
                navbar.classList.remove('navbar--scrolled');
            }

            // Don't do hide/show while hero is visible
            if (isHeroVisible) {
                navbar.classList.remove('navbar--hidden');
                navbar.classList.remove('navbar--visible');
                lastScrollY = currentScrollY;
                return;
            }

            const diff = currentScrollY - lastScrollY;

            if (diff > scrollThreshold) {
                // Scrolling DOWN → hide
                navbar.classList.add('navbar--hidden');
                navbar.classList.remove('navbar--visible');
                lastScrollY = currentScrollY;
            } else if (diff < -scrollThreshold) {
                // Scrolling UP → show
                navbar.classList.remove('navbar--hidden');
                navbar.classList.add('navbar--visible');
                lastScrollY = currentScrollY;
            }

            // At the very top, reset
            if (currentScrollY <= 0) {
                navbar.classList.remove('navbar--hidden');
                navbar.classList.remove('navbar--visible');
                navbar.classList.remove('navbar--scrolled');
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();

        // ===== CUSTOM MOBILE MENU TOGGLE =====
        const toggler = navbar.querySelector('.navbar-toggler');
        const navbarCollapse = document.getElementById('navbarNav');
        const backdrop = document.getElementById('mobileMenuBackdrop');

        if (!toggler || !navbarCollapse) return;

        const openMenu = () => {
            navbarCollapse.classList.add('show');
            toggler.setAttribute('aria-expanded', 'true');
            document.body.classList.add('menu-open');
            document.documentElement.classList.add('menu-open');
            if (backdrop) backdrop.classList.add('show');
            if (this.lenis) this.lenis.stop();
        };

        const closeMenu = () => {
            navbarCollapse.classList.remove('show');
            navbarCollapse.classList.remove('submenu-open');
            toggler.setAttribute('aria-expanded', 'false');
            document.body.classList.remove('menu-open');
            document.documentElement.classList.remove('menu-open');
            if (backdrop) backdrop.classList.remove('show');
            if (this.lenis) this.lenis.start();

            // Reset any open submenus
            const openDropdowns = navbarCollapse.querySelectorAll('.nav-item--dropdown.show');
            openDropdowns.forEach(dd => dd.classList.remove('show'));
        };

        // Hamburger toggle
        toggler.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const isOpen = navbarCollapse.classList.contains('show');
            if (isOpen) {
                closeMenu();
            } else {
                openMenu();
            }
        });

        // Backdrop click to close
        if (backdrop) {
            backdrop.addEventListener('click', () => {
                closeMenu();
            });
        }

        // ===== MOBILE SUBMENU LAYER =====
        const dropdownItems = navbarCollapse.querySelectorAll('.nav-item--dropdown');
        dropdownItems.forEach(item => {
            const link = item.querySelector('.nav-link');
            if (!link) return;

            link.addEventListener('click', (e) => {
                // Only handle on mobile
                if (window.innerWidth >= 1200) return;

                e.preventDefault();
                e.stopPropagation();

                const isOpen = item.classList.contains('show');

                if (isOpen) {
                    // Close submenu → back to normal menu
                    item.classList.remove('show');
                    navbarCollapse.classList.remove('submenu-open');
                } else {
                    // Close any other open submenu first
                    dropdownItems.forEach(dd => dd.classList.remove('show'));
                    // Open this submenu → submenu layer mode
                    item.classList.add('show');
                    navbarCollapse.classList.add('submenu-open');
                }
            });
        });

        // Close menu on window resize to desktop
        window.addEventListener('resize', App.debounce(() => {
            if (window.innerWidth >= 1200 && navbarCollapse.classList.contains('show')) {
                closeMenu();
            }
        }, 250));
    },

    // ========== SWIPER INITIALIZATION ==========
    initSwiper() {
        const swiperEl = document.querySelector('.expertise-swiper');
        if (!swiperEl) return;

        new Swiper('.expertise-swiper', {
            slidesPerView: 1,
            spaceBetween: 20,
            loop: true,
            grabCursor: true,

            breakpoints: {
                576: { slidesPerView: 1.5, spaceBetween: 20 },
                768: { slidesPerView: 2.5, spaceBetween: 24 },
                1024: { slidesPerView: 3.5, spaceBetween: 30 },
                1400: { slidesPerView: 4, spaceBetween: 30 }
            },

            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },

            pagination: {
                el: '.swiper-pagination',
                clickable: true,
                dynamicBullets: true,
            },

            // autoplay: {
            //     delay: 5000,
            //     disableOnInteraction: false,
            //     pauseOnMouseEnter: true,
            // },
        });
    },

    // ========== TEAM SWIPER INITIALIZATION ==========
    initTeamSwiper() {
        const teamSwiperEl = document.querySelector('.team-swiper');
        if (!teamSwiperEl) return;

        new Swiper('.team-swiper', {
            slidesPerView: 1,
            spaceBetween: 20,
            grabCursor: true,
            loop: true,

            breakpoints: {
                576: { slidesPerView: 1.5, spaceBetween: 20 },
                768: { slidesPerView: 2, spaceBetween: 24 },
                1024: { slidesPerView: 3, spaceBetween: 30 },
            },

            navigation: {
                nextEl: '.team-swiper__next',
                prevEl: '.team-swiper__prev',
            },
        });
    },

    // ========== PARTNER LOGOS SWIPER ==========
    initPartnerSwiper() {
        const partnerSwiperEl = document.querySelector('.partner-swiper');
        if (!partnerSwiperEl) return;

        new Swiper('.partner-swiper', {
            slidesPerView: 1,
            spaceBetween: 20,
            grabCursor: true,
            loop: true,
            autoplay: {
                delay: 1500,
                disableOnInteraction: false,
            },

            breakpoints: {
                768: { slidesPerView: 3, spaceBetween: 30 },
                1024: { slidesPerView: 5, spaceBetween: 40 },
            },
        });
    },

    // ========== RELATED SERVICES SWIPER ==========
    initRelatedSwiper() {
        const el = document.querySelector('.related-swiper');
        if (!el) return;

        new Swiper('.related-swiper', {
            slidesPerView: 1,
            spaceBetween: 20,
            grabCursor: true,

            breakpoints: {
                576: { slidesPerView: 1.5, spaceBetween: 20 },
                768: { slidesPerView: 2.5, spaceBetween: 24 },
                1024: { slidesPerView: 3.5, spaceBetween: 30 },
            },

            pagination: {
                el: '.related-swiper__pagination',
                clickable: true,
                dynamicBullets: true,
            },
        });
    },

    // ========== EXPERTISE CARDS HOVER EFFECT ==========
    initExpertiseCards() {
        const expertiseCards = document.querySelectorAll('.expertise-card');

        expertiseCards.forEach(card => {
            const bgImage = card.getAttribute('data-bg');
            if (bgImage) {
                const styleEl = document.createElement('style');
                styleEl.textContent = `
                    .expertise-card[data-bg="${bgImage}"]::before {
                        background-image: url(${bgImage});
                    }
                `;
                document.head.appendChild(styleEl);
            }
        });
    },

    // ========== TAB SECTION FUNCTIONALITY ==========
    initTabSection() {
        const tabItems = document.querySelectorAll('.tab-item');
        const tabContents = document.querySelectorAll('.tab-content-item');

        if (!tabItems.length || !tabContents.length) return;

        tabItems.forEach(tab => {
            tab.addEventListener('click', () => {
                const tabId = tab.getAttribute('data-tab');

                // Remove active class from all tabs
                tabItems.forEach(t => t.classList.remove('active'));
                // Add active class to clicked tab
                tab.classList.add('active');

                // Remove active class from all content items
                tabContents.forEach(content => content.classList.remove('active'));
                // Add active class to corresponding content
                const activeContent = document.querySelector(`.tab-content-item[data-tab="${tabId}"]`);
                if (activeContent) {
                    activeContent.classList.add('active');
                }
            });
        });
    },

    // ========== SCROLL TO TOP BUTTON ==========
    initScrollToTop() {
        const scrollToTopBtn = document.getElementById('scrollToTop');
        if (!scrollToTopBtn) return;

        const scrollThreshold = 250;

        // Show/hide button based on scroll position
        window.addEventListener('scroll', () => {
            if (window.scrollY > scrollThreshold) {
                scrollToTopBtn.classList.add('visible');
            } else {
                scrollToTopBtn.classList.remove('visible');
            }
        });

        // Scroll to top on click
        scrollToTopBtn.addEventListener('click', () => {
            // Use Lenis if available, otherwise use native scroll
            if (this.lenis) {
                this.lenis.scrollTo(0, {
                    duration: 1.2
                });
            } else {
                window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                });
            }
        });
    },

    // ========== BLOG FILTERS ==========
    initBlogFilters() {
        const blogGrid = document.getElementById('blogGrid');
        if (!blogGrid) return; // Only run on blog page

        const searchInput = document.getElementById('blogSearch');
        const articles = blogGrid.querySelectorAll('.article-card');
        const noResults = document.getElementById('noResults');
        const categoryToggle = document.getElementById('categoryToggle');
        const sortToggle = document.getElementById('sortToggle');
        const categoryRow = document.getElementById('categoryRow');
        const sortRow = document.getElementById('sortRow');

        if (!searchInput || !sortToggle) return;

        let currentCategory = 'all';
        let currentSort = 'latest';

        // Filter and sort articles
        const filterAndSortArticles = () => {
            const searchTerm = searchInput.value.toLowerCase().trim();
            const articlesArray = Array.from(articles);

            // Filter
            articlesArray.forEach(article => {
                const category = article.dataset.category;
                const title = article.dataset.title.toLowerCase();
                const excerpt = article.querySelector('.article-card__excerpt').textContent.toLowerCase();

                const matchesCategory = currentCategory === 'all' || category === currentCategory;
                const matchesSearch = searchTerm === '' ||
                    title.includes(searchTerm) ||
                    excerpt.includes(searchTerm);

                if (matchesCategory && matchesSearch) {
                    article.style.display = '';
                    article.classList.remove('hidden');
                } else {
                    article.style.display = 'none';
                    article.classList.add('hidden');
                }
            });

            // Sort visible articles
            const visibleArticles = articlesArray.filter(a => !a.classList.contains('hidden'));

            visibleArticles.sort((a, b) => {
                if (currentSort === 'latest') {
                    return new Date(b.dataset.date) - new Date(a.dataset.date);
                } else if (currentSort === 'az') {
                    return a.dataset.title.localeCompare(b.dataset.title);
                }
                return 0;
            });

            // Reorder in DOM
            visibleArticles.forEach(article => {
                blogGrid.appendChild(article);
            });

            // Show/hide no results message
            const hasResults = visibleArticles.length > 0;
            noResults.style.display = hasResults ? 'none' : 'block';
            blogGrid.style.display = hasResults ? '' : 'none';
        };

        // Category toggle
        if (categoryToggle && categoryRow) {
            categoryToggle.addEventListener('click', () => {
                const isActive = categoryRow.classList.contains('active');

                // Close sort row if open
                sortRow.classList.remove('active');
                sortToggle.classList.remove('active');

                // Toggle category row
                if (isActive) {
                    categoryRow.classList.remove('active');
                    categoryToggle.classList.remove('active');
                } else {
                    categoryRow.classList.add('active');
                    categoryToggle.classList.add('active');
                }
            });
        }

        // Sort toggle
        sortToggle.addEventListener('click', () => {
            const isActive = sortRow.classList.contains('active');

            // Close category row if open
            if (categoryRow) categoryRow.classList.remove('active');
            if (categoryToggle) categoryToggle.classList.remove('active');

            // Toggle sort row
            if (isActive) {
                sortRow.classList.remove('active');
                sortToggle.classList.remove('active');
            } else {
                sortRow.classList.add('active');
                sortToggle.classList.add('active');
            }
        });

        // Category filter buttons
        if (categoryRow) {
            const categoryBtns = categoryRow.querySelectorAll('[data-category]');
            categoryBtns.forEach(btn => {
                btn.addEventListener('click', function () {
                    categoryBtns.forEach(b => b.classList.remove('active'));
                    this.classList.add('active');
                    currentCategory = this.dataset.category;
                    filterAndSortArticles();
                });
            });
        }

        // Sort filter buttons
        const sortBtns = sortRow.querySelectorAll('[data-sort]');
        sortBtns.forEach(btn => {
            btn.addEventListener('click', function () {
                sortBtns.forEach(b => b.classList.remove('active'));
                this.classList.add('active');
                currentSort = this.dataset.sort;
                filterAndSortArticles();
            });
        });

        // Search input
        searchInput.addEventListener('input', filterAndSortArticles);
    },

    // ========== CONTACT PAGE SERVICE PILLS ==========
    initContactPills() {
        const container = document.getElementById('servicePills');
        if (!container) return;

        const pills = container.querySelectorAll('.contact-section__pill');
        const hiddenField = document.getElementById('servicioHidden');

        pills.forEach(pill => {
            pill.addEventListener('click', () => {
                pills.forEach(p => p.classList.remove('active'));
                pill.classList.add('active');

                // Update hidden field (fallback form or CF7 hidden field)
                if (hiddenField) {
                    hiddenField.value = pill.dataset.value;
                }
            });
        });
    },

    // ========== NUMBERS COUNTER ANIMATION ==========
    initCounterAnimation() {
        const section = document.querySelector('.numbers-section');
        if (!section) return;

        const counters = section.querySelectorAll('.stat-number[data-count]');
        if (!counters.length) return;

        let animated = false;

        const animateCounters = () => {
            if (animated) return;
            animated = true;

            counters.forEach(counter => {
                const target = parseInt(counter.dataset.count, 10);
                const prefix = counter.dataset.prefix || '';
                const obj = { val: 0 };

                gsap.to(obj, {
                    val: target,
                    duration: 3.5,
                    ease: 'power2.out',
                    onUpdate: () => {
                        counter.textContent = prefix + Math.round(obj.val);
                    },
                });
            });
        };

        // Set initial text
        counters.forEach(counter => {
            counter.textContent = (counter.dataset.prefix || '') + '0';
        });

        ScrollTrigger.create({
            trigger: section,
            start: 'top 85%',
            end: 'bottom 15%',
            onEnter: animateCounters,      // scrolling down into view
            onEnterBack: animateCounters,  // scrolling up into view
        });
    },
};

// ============================================================
// GSAP ANIMATIONS MODULE
// ============================================================
const GSAPAnimations = {
    init() {
        gsap.registerPlugin(ScrollTrigger);

        // 1. Pinned sections FIRST (crucial order per Villa Rosa pattern)
        this.aboutMethodAnimation();

        // 2. Force refresh after pinned sections
        ScrollTrigger.refresh();

        // 3. Then other animations
        this.heroAnimation();
        this.aboutHeroAnimation();
        this.parallaxSection();
        this.servizioParallax();
        this.footerCtaParallax();

        window.addEventListener('load', () => {
            ScrollTrigger.refresh();
        });

        // Handle navigation from other pages (bfcache)
        window.addEventListener('pageshow', (event) => {
            if (event.persisted) {
                ScrollTrigger.refresh(true);
            }
        });
    },

    // ========== HERO SECTION ANIMATION ==========
    heroAnimation() {
        const heroWrapper = document.querySelector('.hero-wrapper');
        const heroSection = document.querySelector('.hero-section');
        const heroWrapOut = document.querySelector('.hero-wrap-out');
        const heroOverlay = document.querySelector('.hero-overlay');
        const heroRevealSection = document.querySelector('.hero-reveal-section');
        const heroRevealContent = document.querySelector('.hero-reveal-content');
        const heroRevealTitle = document.querySelector('.hero-reveal-title');
        const heroRevealText = document.querySelector('.hero-reveal-text');
        const circleLeft = document.getElementById('heroCircleLeft');
        const circleRight = document.getElementById('heroCircleRight');

        if (!heroSection || !heroWrapOut || !heroWrapper) return;

        // ========== HERO CONTENT FADE IN (replaces CSS animation) ==========
        const heroContentWrapper = document.querySelector('.hero-content-wrapper');
        const heroButtons = document.querySelector('.hero__buttons');

        // Animate hero content elements on page load
        if (heroContentWrapper) {
            gsap.to(heroContentWrapper, {
                opacity: 1,
                y: 0,
                duration: 0.9,
                ease: 'power2.out',
                delay: 0.1
            });
        }

        if (heroButtons) {
            gsap.to(heroButtons, {
                opacity: 1,
                y: 0,
                duration: 0.9,
                ease: 'power2.out',
                delay: 0.3
            });
        }

        // Skip animation on mobile/tablet
        if (window.innerWidth < 1200) {
            gsap.set(heroSection, { clipPath: 'none' });
            if (heroRevealContent) gsap.set(heroRevealContent, { opacity: 1, y: 0 });
            return;
        }

        if (!circleLeft || !circleRight) return;

        // Colored overlay circles
        const overlayLeft = document.getElementById('heroOverlayLeft');
        const overlayRight = document.getElementById('heroOverlayRight');

        // ========== DUAL CIRCLE CONFIGURATION (pixel coords) ==========
        const heroRect = heroSection.getBoundingClientRect();
        const centerY = heroRect.height / 2;
        const finalR = heroRect.height * 0.18; // Circle radius = 18% of viewport height (10% smaller than original 20%)
        const gap = finalR * 0.12; // Small gap between circles (proportional, distance stays similar)
        const cxLeft = heroRect.width / 2 - finalR - gap / 2;
        const cxRight = heroRect.width / 2 + finalR + gap / 2;
        const startR = Math.max(heroRect.width, heroRect.height) * 1.5; // Large enough to cover everything

        // Initial state: both circles cover entire viewport
        gsap.set(circleLeft, { attr: { cx: cxLeft, cy: centerY, r: startR } });
        gsap.set(circleRight, { attr: { cx: cxRight, cy: centerY, r: startR } });
        gsap.set(heroSection, { clipPath: 'url(#heroDoubleCircle)' });

        // Position colored overlays (hidden initially)
        if (overlayLeft) {
            gsap.set(overlayLeft, {
                width: finalR * 2,
                height: finalR * 2,
                left: cxLeft - finalR,
                top: centerY - finalR,
                opacity: 0
            });
        }
        if (overlayRight) {
            gsap.set(overlayRight, {
                width: finalR * 2,
                height: finalR * 2,
                left: cxRight - finalR,
                top: centerY - finalR,
                opacity: 0
            });
        }

        // ========== HERO WRAPPER TIMELINE (pinned) ==========
        const heroTl = gsap.timeline({
            scrollTrigger: {
                trigger: heroWrapper,
                start: 'top top',
                end: '+=200%',
                pin: true,
                scrub: 1,
                anticipatePin: 1,
            }
        });

        // Animation sequence
        heroTl
            // ========== PHASE 1: Hero content fade out ==========
            .to(heroWrapOut, {
                opacity: 0,
                y: -30,
                duration: 0.2,
                ease: 'power2.inOut'
            })
            // Darken overlay
            .to(heroOverlay, {
                opacity: 0.5,
                duration: 0.15,
                ease: 'power2.inOut'
            }, '<')

            // ========== PHASE 2: Clip-path shrinks to dual circles ==========
            .to([circleLeft, circleRight], {
                attr: { r: finalR },
                duration: 0.4,
                ease: 'power2.inOut'
            }, '<0.1')
            // Colored overlays fade in as circles form
            .to([overlayLeft, overlayRight].filter(Boolean), {
                opacity: 1,
                duration: 0.3,
                ease: 'power2.out'
            }, '-=0.15')

            // ========== PHASE 3: Circles shrink, rise and vanish ==========
            // Clip-path circles shrink to 0 while rising upward
            .to([circleLeft, circleRight], {
                attr: { r: 0, cy: centerY - 250 },
                duration: 0.35,
                ease: 'power2.in'
            })
            // Colored overlays shrink and rise in sync
            .to([overlayLeft, overlayRight].filter(Boolean), {
                scale: 0,
                y: -250,
                transformOrigin: 'center center',
                duration: 0.35,
                ease: 'power2.in'
            }, '<');

        // ========== HERO REVEAL SECTION (synced with hero pin scroll) ==========
        if (heroRevealSection && heroRevealContent) {
            // Make visible now that GSAP is ready (CSS hides it to prevent white flash)
            gsap.set(heroRevealSection, { visibility: 'visible' });

            // Section slides up during the hero pin, arriving centered as circles vanish
            gsap.fromTo(heroRevealSection, {
                y: 200,
            }, {
                y: 0,
                ease: 'none',
                scrollTrigger: {
                    trigger: heroWrapper,
                    start: 'top top',
                    end: '+=200%',
                    scrub: 1,
                }
            });

            // Content fades in during Phase 3 (synced with circle dissolve) 
            heroTl.to(heroRevealContent, {
                opacity: 1,
                y: 0,
                duration: 0.3,
                ease: 'power2.out'
            }, '-=0.25');
        }

        // Handle resize
        let wasDesktop = window.innerWidth >= 1200;
        window.addEventListener('resize', App.debounce(() => {
            const isDesktop = window.innerWidth >= 1200;

            if (wasDesktop !== isDesktop) {
                ScrollTrigger.refresh(true);
                wasDesktop = isDesktop;
            } else {
                ScrollTrigger.refresh();
            }
        }, 250));
    },

    // ========== ABOUT HERO ANIMATION ==========
    aboutHeroAnimation() {
        const aboutHero = document.querySelector('.about-hero');
        if (!aboutHero) return;

        const textBlock = aboutHero.querySelector('.about-hero__text');
        const images = aboutHero.querySelectorAll('.about-hero__img');

        // --- Title fade-in on page load ---
        if (textBlock) {
            gsap.to(textBlock, {
                opacity: 1,
                y: 0,
                duration: 1,
                ease: 'power2.out',
                delay: 0.2
            });
        }

        // --- Images fade-in stagger, then parallax ---
        if (images.length) {
            const createParallax = () => {
                // --- Parallax on scroll (desktop only) ---
                if (window.innerWidth >= 768) {
                    images.forEach(img => {
                        const speed = parseFloat(img.dataset.parallaxSpeed) || 0.1;

                        gsap.to(img, {
                            y: () => -window.innerHeight * speed,
                            ease: 'none',
                            scrollTrigger: {
                                trigger: aboutHero,
                                start: 'top top',
                                end: 'bottom top',
                                scrub: 1,
                            }
                        });
                    });
                }
            };

            gsap.fromTo(images, {
                opacity: 0,
                y: 60
            }, {
                opacity: 1,
                y: 0,
                duration: 0.9,
                stagger: 0.15,
                ease: 'power2.out',
                delay: 0.5,
                onComplete: createParallax
            });
        }
    },

    // ========== ABOUT METHOD PINNED ANIMATION ==========
    aboutMethodAnimation() {
        const section = document.querySelector('.about-method');
        if (!section) return;

        const wrapper = section.querySelector('.about-method__wrapper');
        const icons = section.querySelectorAll('.about-method__icon');
        const steps = section.querySelectorAll('.about-method__step');

        if (!wrapper || steps.length === 0) return;

        // Pinned animation runs on all screen sizes

        // Timeline with pin (Villa Rosa pattern)
        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                start: 'top top',
                end: `+=${steps.length * 100}%`,
                pin: wrapper,
                scrub: 1,
                anticipatePin: 1,
                pinSpacing: true,
                refreshPriority: 1,
                invalidateOnRefresh: true,
            }
        });

        // Phase 0: Icons animate in (0-5%)
        tl.to(icons, {
            opacity: 1,
            y: 0,
            stagger: 0.02,
            duration: 0.05,
            ease: 'power2.out'
        }, 0);

        // Helper: set active dot
        const setActiveDot = (index) => {
            icons.forEach((icon, j) => {
                icon.classList.toggle('about-method__icon--active', j === index);
            });
        };

        // Phase 1+: Each step fades in, holds, then fades out upward
        const stepDuration = 1 / steps.length; // Divide timeline evenly

        steps.forEach((step, i) => {
            const startTime = 0.05 + (i * stepDuration);
            const holdEnd = startTime + stepDuration * 0.6;

            // Fade in from below + activate dot
            tl.fromTo(step, {
                opacity: 0,
                y: 40
            }, {
                opacity: 1,
                y: 0,
                duration: stepDuration * 0.3,
                ease: 'power2.out',
                onStart: () => setActiveDot(i),
                onReverseComplete: () => setActiveDot(Math.max(0, i - 1))
            }, startTime);

            // Fade out upward (except last step)
            if (i < steps.length - 1) {
                tl.to(step, {
                    opacity: 0,
                    y: -40,
                    duration: stepDuration * 0.3,
                    ease: 'power2.in'
                }, holdEnd);
            }
        });
    },

    // ========== HOMEPAGE PARALLAX IMAGE SECTION ==========
    parallaxSection() {
        const section = document.querySelector('.parallax-section');
        if (!section) return;

        const img = section.querySelector('.parallax-section__image');
        if (!img) return;

        gsap.fromTo(img, {
            y: '-15%',
        }, {
            y: '15%',
            ease: 'none',
            scrollTrigger: {
                trigger: section,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1,
            }
        });
    },

    // ========== FOOTER CTA MAP PARALLAX ==========
    footerCtaParallax() {
        const mapImg = document.querySelector('[data-parallax-cta]');
        if (!mapImg) return;

        gsap.fromTo(mapImg, {
            y: '-10%',
        }, {
            y: '10%',
            ease: 'none',
            scrollTrigger: {
                trigger: mapImg.closest('.footer-cta'),
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1,
            }
        });
    },

    // ========== SERVIZIO HERO PARALLAX ==========
    servizioParallax() {
        const parallaxEl = document.querySelector('.servizio-hero__image[data-parallax]');
        if (!parallaxEl) return;

        const img = parallaxEl.querySelector('img');
        if (!img) return;

        gsap.to(img, {
            y: '15%',
            ease: 'none',
            scrollTrigger: {
                trigger: parallaxEl,
                start: 'top bottom',
                end: 'bottom top',
                scrub: 1,
            }
        });
    },

    // ========== UTILITY METHODS FOR FUTURE USE ==========

    sectionReveal(selector) {
        const sections = document.querySelectorAll(selector);

        sections.forEach(section => {
            gsap.from(section, {
                scrollTrigger: {
                    trigger: section,
                    start: 'top 80%',
                    toggleActions: 'play none none reverse'
                },
                opacity: 0,
                y: 50,
                duration: 1,
                ease: 'power3.out'
            });
        });
    },

    staggerElements(containerSelector, itemSelector) {
        const containers = document.querySelectorAll(containerSelector);

        containers.forEach(container => {
            const items = container.querySelectorAll(itemSelector);

            gsap.from(items, {
                scrollTrigger: {
                    trigger: container,
                    start: 'top 75%',
                },
                opacity: 0,
                y: 30,
                stagger: 0.1,
                duration: 0.8,
                ease: 'power2.out'
            });
        });
    }
};

// ============================================================
// INITIALIZE APP
// ============================================================
document.addEventListener('DOMContentLoaded', () => {
    App.init();
});
